import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {withoutSupersededAaSequenceNodes} from 'graphql-client/WithoutSupersededAaSequenceNode';
import {
  NEW_PREDICTION_AA_SEQUENCE_INPUTS_QUERY_WRAPPER,
  NEW_PREDICTION_MOLECULE_INPUTS_QUERY_WRAPPER,
} from 'model-hub/ModelHubQueries';
import APIUtil from 'util/APIUtil';

import {makeEntityComponent, type PredictionComponent} from '../newPredictionState';

export interface FetchedEntityComponents {
  // In the order the entities were picked.
  components: Array<PredictionComponent>;
  // Picked entities that came back inaccessible or unreadable, e.g. archived or unshared since the search ran.
  unavailableCount: number;
}

/**
 * Load what each picked entity contributes as a component: its residues or SMILES, for the same
 * length and charset checks a typed value gets, and whether the user may predict on it.
 */
export async function fetchEntityComponents(entityIds: Array<string>): Promise<FetchedEntityComponents> {
  const moleculeIds = entityIds.filter((id) => APIUtil.isMolecule(id));
  const aaSequenceIds = entityIds.filter((id) => !APIUtil.isMolecule(id));
  // Always from the network: a cached copy can predate the entity being edited or unshared, and this is
  // what the inputs are validated against.
  const [aaSequenceResult, moleculeResult] = await Promise.all([
    aaSequenceIds.length > 0
      ? doQuerySafe(NEW_PREDICTION_AA_SEQUENCE_INPUTS_QUERY_WRAPPER, {
          fetchPolicy: 'network-only',
          variables: {ids: aaSequenceIds},
        })
      : null,
    moleculeIds.length > 0
      ? doQuerySafe(NEW_PREDICTION_MOLECULE_INPUTS_QUERY_WRAPPER, {
          fetchPolicy: 'network-only',
          variables: {moleculeIds},
        })
      : null,
  ]);

  const componentsById = new Map<string, PredictionComponent>();
  // Unreadable entities count as unavailable: a share link can return one that `accessibleObjs` keeps.
  for (const aaSequence of accessibleObjs(
    withoutSupersededAaSequenceNodes(aaSequenceResult?.aaSequences ?? [])
  )) {
    if (!aaSequence.permissions.readable) {
      continue;
    }
    componentsById.set(
      aaSequence.id,
      makeEntityComponent('aminoAcids', aaSequence.experimentalCurrentVersion.aminoAcids, {
        id: aaSequence.id,
        name: aaSequence.name,
        // Matches the legacy Data tab: only an explicit grant allows prediction.
        predictable: aaSequence.permissions.structurePredictionEditable === true,
      })
    );
  }
  for (const molecule of accessibleObjs(moleculeResult?.molecules ?? [])) {
    if (!molecule.permissions.readable) {
      continue;
    }
    componentsById.set(
      molecule.id,
      makeEntityComponent('smiles', molecule.currentVersion.chemicalStructure.canonicalSmiles, {
        id: molecule.id,
        name: molecule.name,
        predictable: true,
      })
    );
  }

  const components = entityIds.flatMap((id) => {
    const component = componentsById.get(id);
    return component == null ? [] : [component];
  });
  return {components, unavailableCount: entityIds.length - components.length};
}
